import React from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import { platformErrorsFormat } from "@/features/observability/platform-errors/components/platform-errors-format";
import { PLATFORM_SEVERITIES, type PlatformSeverity, type PlatformErrorsSeriesBucket } from "@/features/observability/platform-errors/types/platform-errors.types";

type Props = {
    buckets: PlatformErrorsSeriesBucket[];
    bucketMs: number;
    rangeMs: number;
    lowerBound: boolean;
};

type Row = { start: number; end: number; count: number | null; values: Partial<Record<PlatformSeverity, number | null>> };

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const AXIS_COLOR = "#64748B";
const GRID_COLOR = "#E6E6E6";

// Backend contract: a null bucket is unknown (a gap in every severity, never a zero bar); inside a returned
// bucket a severity absent from its breakdown is a measured 0.
const rowOf = (bucket: PlatformErrorsSeriesBucket): Row => {
    const values: Partial<Record<PlatformSeverity, number | null>> = {};
    PLATFORM_SEVERITIES.forEach((severity) => {
        const entry = bucket.bySeverity.find((candidate) => candidate.severity === severity);
        values[severity] = bucket.count === null ? null : entry === undefined ? 0 : entry.count;
    });
    return { start: bucket.start, end: bucket.end, count: bucket.count, values };
};

const describe = (row: Row, lowerBound: boolean): string => {
    const span = `${frontendErrorsFormat.dateTime(row.start)}–${frontendErrorsFormat.hourMinute(row.end)}`;
    const count = row.count === null ? "sin dato" : `${frontendErrorsFormat.count(row.count)} ocurrencias${lowerBound ? " (mín.)" : ""}`;
    return `${span} · ${count}`;
};

export function PlatformErrorsSeriesChart({ buckets, bucketMs, rangeMs, lowerBound }: Props) {
    const severities = PLATFORM_SEVERITIES;
    const rows = buckets.map(rowOf);
    const tick = (value: number): string => (rangeMs >= WEEK_MS ? frontendErrorsFormat.day(value) : frontendErrorsFormat.hourMinute(value));

    return (
        <div className="fe-series">
            <div className="fe-card__hd">
                <div className="fe-card__title">{`Ocurrencias por ${frontendErrorsFormat.bucketUnit(bucketMs)}`}</div>
                <div className="fe-legend">
                    {severities.map((severity) => (
                        <span key={severity} className="fe-legend__item">
                            <span className="fe-swatch" style={{ background: platformErrorsFormat.severityStyle(severity).color }} />
                            {platformErrorsFormat.severityStyle(severity).label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="fe-series__plot">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 4" vertical={false} />
                        <XAxis dataKey="start" tickFormatter={tick} tick={{ fontSize: 10, fill: AXIS_COLOR }} axisLine={false} tickLine={false} minTickGap={16} />
                        <YAxis
                            allowDecimals={false}
                            tickFormatter={(value: number) => frontendErrorsFormat.count(value)}
                            tick={{ fontSize: 10, fill: AXIS_COLOR }}
                            axisLine={false}
                            tickLine={false}
                            width={48}
                        />
                        <Tooltip
                            cursor={{ fill: "rgba(28, 175, 255, 0.08)" }}
                            content={({ active, label }) => {
                                if (active !== true) return null;
                                const row = rows.find((candidate) => candidate.start === label);
                                if (row === undefined) return null;
                                return <div className="fe-tooltip">{describe(row, lowerBound)}</div>;
                            }}
                        />
                        {severities.map((severity) => (
                            <Bar
                                key={severity}
                                name={severity}
                                dataKey={(row: Row) => row.values[severity] ?? null}
                                stackId="severities"
                                fill={platformErrorsFormat.severityStyle(severity).color}
                                isAnimationActive={false}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
